import { Injectable } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, defer, finalize, map, Observable } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { ResourceStatus, ResourceType } from '@completion/enums';
import { UserFacade } from '../facades/user.facade';
import { getCurrentProject, getCurrentUser, getProjectAccess, getProjectLoading, getProjects, getResourceState } from '../store/selectors';
import { ProjectsLoad } from '../store/actions';

@Injectable({
  providedIn: 'root',
})

/** Core data guard will make sure current user and current project is selected before rest of component tree loads */
export class CoreDataGuard implements CanActivate {
  isProjectLoaded$ = defer(() => this.store.select(getCurrentProject).pipe(map((project) => !!project)));
  isUserLoaded$ = defer(() => this.store.select(getCurrentUser).pipe(map((user) => user.id !== 0)));
  isProjectAccessLoaded$ = defer(() => this.store.select(getProjectAccess).pipe(map((access) => !!access)));
  projects$ = defer(() => this.store.select(getProjects));
  isProjectsLoading$ = defer(() => this.store.select(getProjectLoading));
  currentUserResourceState$ = defer(() => this.store.select(getResourceState, ResourceType.UserGetCurrentUser));
  projectsResourceState$ = defer(() => this.store.select(getResourceState, ResourceType.GetProjects));
  private hasRequestedUser = false;
  private hasRequestedProjects = false;

  private static isServiceUnavailableStatus(status?: number): boolean {
    return status === 0 || (typeof status === 'number' && status >= 500);
  }

  constructor(
    private store: Store,
    private userFacade: UserFacade,
    private router: Router,
  ) {}

  canActivate(): Observable<boolean> {
    return combineLatest([
      this.isProjectLoaded$,
      this.isUserLoaded$,
      this.isProjectAccessLoaded$,
      this.projects$,
      this.isProjectsLoading$,
      this.currentUserResourceState$,
      this.projectsResourceState$,
    ]).pipe(
      tap(([projectLoaded, userLoaded, _projectAccessLoaded, _projects, isProjectsLoading]) => {
        if (!userLoaded && !this.hasRequestedUser) {
          this.hasRequestedUser = true;
          void this.userFacade.init();
        }

        if (!projectLoaded && !isProjectsLoading && !this.hasRequestedProjects) {
          this.hasRequestedProjects = true;
          this.store.dispatch(new ProjectsLoad());
        }
      }),
      map(([projectLoaded, userLoaded, projectAccessLoaded, projects, isProjectsLoading, currentUserState, projectsState]) => {
        const currentUserFailed = currentUserState.status === ResourceStatus.Failure;
        const projectsFailed = projectsState.status === ResourceStatus.Failure;

        if (currentUserFailed || projectsFailed) {
          if (
            CoreDataGuard.isServiceUnavailableStatus(currentUserState.lastErrorStatusNumber) ||
            CoreDataGuard.isServiceUnavailableStatus(projectsState.lastErrorStatusNumber)
          ) {
            void this.router.navigate(['/service-unavailable']);
            return false; // done: service unavailability
          }

          void this.router.navigate(['/no-project-access']);
          return false; // done: API/load error
        }

        // Navigate to no-project-access only when BOTH success signals have been processed:
        //   - projectsFetched (ResourceStatus.Success): set by ResourceSuccess in trackResource tap
        //   - !isProjectsLoading (loading=false): set by the ProjectsLoadSuccess reducer
        // These fire as two separate dispatches (tap then switchMap in trackResource), so requiring
        // both ensures the actual project data is in the store before we conclude the list is empty.
        // !projectLoaded guards against the case where projects did load but currentProject is still
        // being set (e.g. during a project switch).
        const projectsFetched = projectsState.status === ResourceStatus.Success;
        if (userLoaded && projectsFetched && !isProjectsLoading && !projectLoaded && projects.length === 0) {
          void this.router.navigate(['/no-project-access']);
          return false; // done: user genuinely has no project assignments
        }

        if (projectLoaded && userLoaded && projectAccessLoaded) {
          return true; // done: all OK
        }

        return null; // still loading => keep going
      }),
      filter((canActivate: boolean | null): canActivate is boolean => canActivate !== null),
      take(1),
      finalize(() => this.resetBootstrapFlags()),
    );
  }

  private resetBootstrapFlags(): void {
    this.hasRequestedUser = false;
    this.hasRequestedProjects = false;
  }
}
